// src/views/operateur/VeilleManques.jsx — CE QUI MANQUE, CE QUI NE SE PASSE PAS.
//
// Même doctrine que VeilleCautions : rendu hors de l'interrupteur de
// l'opérateur (ces manques existent que l'opérateur soit allumé ou non), et
// AUCUN bouton d'action — relancer un voyageur, clore un ménage ou juger un
// incident se fait dans l'écran concerné, où vivent les gardes.
//
// Les seuils viennent de taux de base MESURÉS en production (voir
// lib/veille-manques.js). Ce composant ne décide de rien : il affiche.

import { useState, useEffect, useCallback } from "react";
import { fetchVeilleManques, poserSilenceVeille } from "../../lib/veille-api";

const TITRES = {
  fil_sans_reponse: "Message sans réponse",
  menage_jamais_clos: "Ménage jamais clos",
  depart_sans_menage: "Départ sans ménage",
  incident_jamais_juge: "Incident non tranché",
  formulaire_non_rempli: "Formulaire non rempli",
  formulaire_jamais_envoye: "Formulaire jamais envoyé",
  avis_negatif_non_traite: "Avis négatif sans réponse",
  rachat_jamais_fait: "Rachat décidé, jamais fait",
  prospect_abandonne: "Échange laissé sans suite",
  logement_sans_bareme: "Aucun devis possible",
};

export default function VeilleManques({ onAller }) {
  const [etat, setEtat] = useState("chargement");
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [deplie, setDeplie] = useState(false);
  const [enCours, setEnCours] = useState(null); // clé de l'alerte qu'on tait
  const [erreurGeste, setErreurGeste] = useState(null);

  const charger = useCallback(async () => {
    setEtat("chargement");
    setErreur(null);
    try {
      setD(await fetchVeilleManques());
      setEtat("ok");
    } catch (e) {
      console.error("[VeilleManques]", e);
      setErreur(e.message || "lecture impossible");
      setEtat("erreur");
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // « FAIT AILLEURS » — le motif est DEMANDÉ, jamais supposé : un silence sans
  // raison est indistinguable d'un bug six mois plus tard. La période est celle
  // du mois courant, donc le silence expire tout seul le mois prochain.
  async function taire(a) {
    const motif = window.prompt(
      `Pourquoi ne plus voir cette alerte ce mois-ci ?\n\n${a.phrase}`,
      "fait ailleurs",
    );
    if (motif == null || !motif.trim()) return; // annulé : on ne tait rien
    const cle = `${a.type}:${a.id}`;
    setEnCours(cle);
    setErreurGeste(null);
    try {
      await poserSilenceVeille({
        famille: a.type,
        cle: a.id,
        periode: new Date().toISOString().slice(0, 7),
        motif: motif.trim(),
      });
      await charger(); // on relit : l'écran montre l'état réel, pas l'espéré
    } catch (e) {
      console.error("[VeilleManques] taire", e);
      setErreurGeste(e.message || "le silence n'a pas pu être enregistré");
    } finally {
      setEnCours(null);
    }
  }

  if (etat === "chargement") return null;

  if (etat === "erreur") {
    return (
      <div data-testid="veille-manques-erreur"
        style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 10,
          background: "#fdecec", color: "#a12b2b", fontSize: 12 }}>
        Les manques n'ont pas pu être vérifiés ({erreur}). Ce n'est pas « rien à
        signaler » — on n'a pas pu regarder.{" "}
        <button onClick={charger}
          style={{ background: "transparent", border: "none", color: "#a12b2b", fontWeight: 600, textDecoration: "underline", cursor: "pointer", padding: 0 }}>
          Réessayer
        </button>
      </div>
    );
  }

  const alertes = d?.alertes || [];
  const indeterminees = d?.indeterminees || [];
  const incomplet = d?.complet === false;

  if (!alertes.length && !indeterminees.length && !incomplet) {
    return (
      <div data-testid="veille-manques-ok"
        style={{ margin: "0 0 12px", padding: "9px 12px", borderRadius: 10,
          background: "#eef3f8", color: "var(--gris-d)", fontSize: 11.5 }}>
        Rien ne traîne : messages, ménages, incidents et formulaires sont à jour.
      </div>
    );
  }

  const hautes = alertes.filter((a) => a.gravite === "haute").length;
  // Regroupement par famille : dix messages en retard forment UNE ligne à lire,
  // pas dix lignes à faire défiler. Le détail reste dessous.
  const parType = alertes.reduce((acc, a) => {
    (acc[a.type] = acc[a.type] || []).push(a);
    return acc;
  }, {});

  return (
    <div data-testid="veille-manques"
      style={{ margin: "0 0 12px", border: "1px solid #cfd9e6", borderRadius: 10,
        background: "#f7fafd", overflow: "hidden" }}>
      <button onClick={() => setDeplie((v) => !v)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
          padding: "10px 12px", background: "transparent", border: "none", cursor: "pointer",
          fontFamily: "inherit", fontSize: 12.5, color: "var(--navy-d)", fontWeight: 600 }}>
        <span aria-hidden="true">{deplie ? "▾" : "▸"}</span>
        Ce qui ne s'est pas passé — {alertes.length}
        {hautes > 0 && (
          <span style={{ background: "#a12b2b", color: "#fff", borderRadius: 6, padding: "1px 6px", fontSize: 10 }}>
            {hautes} urgent{hautes > 1 ? "s" : ""}
          </span>
        )}
      </button>

      {deplie && (
        <div style={{ padding: "0 12px 12px" }}>
          {incomplet && (
            <div data-testid="veille-manques-incomplet"
              style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: "#fdecec", color: "#a12b2b", fontSize: 11.5 }}>
              Vue INCOMPLÈTE — source(s) illisible(s) : {(d.sources_illisibles || []).join(", ")}.
              Le compte est un minimum, pas un total.
            </div>
          )}

          {erreurGeste && (
            <div data-testid="veille-manques-erreur-geste"
              style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: "#fdecec", color: "#a12b2b", fontSize: 11.5 }}>
              {erreurGeste} — l'alerte est donc TOUJOURS là.
            </div>
          )}

          {d.nb_tues > 0 && (
            // Taire n'est pas supprimer : ce qui est tu se compte, à l'écran.
            <div data-testid="veille-manques-tues"
              style={{ marginBottom: 8, fontSize: 11, color: "var(--gris)" }}>
              {d.nb_tues} alerte{d.nb_tues > 1 ? "s" : ""} tue{d.nb_tues > 1 ? "s" : ""} ce mois-ci
              {" "}(« fait ailleurs »).
            </div>
          )}

          {Object.entries(parType).map(([type, lignes]) => (
            <div key={type} data-testid={`manque-${type}`} style={{ padding: "8px 0", borderTop: "1px solid #e4ebf4" }}>
              <div style={{ fontWeight: 600, fontSize: 12,
                color: lignes.some((l) => l.gravite === "haute") ? "#a12b2b" : "var(--navy-d)" }}>
                {TITRES[type] || type}
                <span style={{ color: "var(--gris)", fontWeight: 400 }}> · {lignes.length}</span>
              </div>
              {lignes.slice(0, 5).map((l) => (
                <div key={l.id} data-testid={`ligne-${l.type}-${l.id}`}
                  style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", fontSize: 11.5, color: "var(--gris-d)", marginTop: 3 }}>
                  <span>
                    {l.logement_id && <span style={{ color: "var(--gris)" }}>{l.logement_id} · </span>}
                    {l.phrase}
                  </span>
                  {/* OUVRIR — la destination vient du moteur, jamais devinée ici. */}
                  {l.destination && onAller && (
                    <button onClick={() => onAller(l.destination)}
                      style={{ background: "transparent", border: "none", color: "var(--navy-d)", fontWeight: 600, fontSize: 11, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                      {l.destination.quoi}
                    </button>
                  )}
                  <button onClick={() => taire(l)} disabled={enCours === `${l.type}:${l.id}`}
                    title="Ne plus voir cette alerte ce mois-ci"
                    style={{ background: "transparent", border: "none", color: "var(--gris)", fontSize: 11, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                    {enCours === `${l.type}:${l.id}` ? "…" : "fait ailleurs"}
                  </button>
                </div>
              ))}
              {lignes.length > 5 && (
                <div style={{ fontSize: 11, color: "var(--gris)", marginTop: 2 }}>
                  … et {lignes.length - 5} autre{lignes.length - 5 > 1 ? "s" : ""}.
                </div>
              )}
            </div>
          ))}

          {indeterminees.length > 0 && (
            <div data-testid="veille-manques-indeterminees"
              style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #e4ebf4", fontSize: 11.5, color: "#8a5b16" }}>
              <strong>{indeterminees.length}</strong> ligne{indeterminees.length > 1 ? "s" : ""} sur
              laquelle je ne peux pas conclure : {indeterminees.map((x) => x.motif).join(" · ")}.
            </div>
          )}

          <div style={{ marginTop: 10, fontSize: 11, color: "var(--gris)" }}>
            Observé, jamais corrigé ici : répondre, clore un ménage ou juger un
            incident se fait dans l'écran concerné.
          </div>
        </div>
      )}
    </div>
  );
}
